import { DropDownList, Loading, TextBox } from "@repo/ui";
import { useMemo } from "react";

import type { LayerFilterProps } from "../hooks/useUpdateLayerFilter";

import { useMapSettings } from "../hooks/mapSettings";
import { useUpdateLayerFilter } from "../hooks/useUpdateLayerFilter";
import { isOfficeLayer, isPositionLayer, isRetailLayer } from "../layers/layerTypes";
import { ClientSideLabel } from "./clientSideLabel";
import { OfficesFilters } from "./officesFilter";
import { RepresentativesFilters } from "./representativesFilter";
import { RetailLocationsFilters } from "./retailLocationsFilters";
import { SalesFilter } from "./salesFilter";

const DEFAULT_MAX_POSITIONS = 3000;

export function MapFilters({ layerId }: { layerId: string }) {
	const { layer, updateFilter } = useUpdateLayerFilter(layerId);
	const mapSettingsQuery = useMapSettings();
	const maxPositions = mapSettingsQuery.data?.maxPositions ?? DEFAULT_MAX_POSITIONS;

	const viewOptions = useMemo(
		() => mapSettingsQuery.data?.layeredMapViews?.map((type) => ({ id: type, value: type })) ?? [],
		[mapSettingsQuery.data],
	);

	if (!layer) return null;

	const handleLimitChange = (value: string) => {
		const limit = parseInt(value, 10);
		if (!isNaN(limit)) updateFilter(Math.min(Math.max(limit, 1), maxPositions), "lastPositions");
	};

	return (
		<Loading loading={mapSettingsQuery.isLoading}>
			<DropDownList
				label="Views"
				value={layer.itemType ?? ""}
				changeHandler={(value) => updateFilter(value, "itemType")}
				values={viewOptions}
				valueProp="id"
				textProp="value"
				hidden={viewOptions.length < 2}
				fullLength
			/>
			<LayerTypeFilters layer={layer} updateFilter={updateFilter} />
			<TextBox
				label={<ClientSideLabel label="Limit" />}
				type="number"
				inputMode="numeric"
				maxlength={4}
				value={layer.lastPositions ?? ""}
				changeHandler={handleLimitChange}
			/>
		</Loading>
	);
}

function LayerTypeFilters(props: LayerFilterProps) {
	const { itemType } = props.layer;
	if (isPositionLayer(itemType)) return <RepresentativesFilters {...props} />;
	if (itemType === "Sales") return <SalesFilter {...props} />;
	if (isRetailLayer(itemType)) return <RetailLocationsFilters {...props} />;
	if (isOfficeLayer(itemType)) return <OfficesFilters {...props} />;
	return null;
}
